// Druckerwächter (internal/druckwaechter, docs/druckwaechter.md): Stand
// über das Homelab-Protokoll (Topic `druckwaechter.stand`, der Dienst setzt
// ihn jeden Takt und bei jeder Änderung; docs/protokoll.md › „Druckerwächter
// über das Protokoll"). GET /v2-api/druckwaechter ist Boot-Hydration und
// Revalidator (und die Antwort auf Stumm/Einstellung). Die Fälle kommen aus
// /v2-api/druckwaechter/faelle und werden neu geholt, sobald der Stand einen
// neuen jüngsten Fall nennt.

import { SvelteMap } from "svelte/reactivity";

import { getJSON, postJSON, putJSON } from "./api";
import type { Einstellung, Fall, WStand } from "./drucker/waechter";
import { wStandAusProtokoll } from "./drucker/waechter-protokoll";
import { loadPersisted, persist } from "./persistent-summary.svelte";
import { abonniere } from "./protokoll/client.svelte";
import type { DruckwaechterStand } from "./protokoll/schema.gen";
import { skaliere } from "./protokoll/skaliere";
import { isoAusMs } from "./protokoll/werte";
import { registerRevalidator } from "./revalidate.svelte";

const STORAGE_KEY = "druckwaechter";

const _s = new SvelteMap<string, WStand>();
const _faelle = new SvelteMap<string, Fall[]>();
let _started = false;
let _faelleFuer: string | undefined;
let _unsubscribe: (() => void) | undefined;

function uebernimm(s: WStand): void {
  if (!s || typeof s.zustand !== "string") return;
  _s.set("current", s);
  persist(STORAGE_KEY, s);
  if (_faelle.has("current") && s.letzter_fall !== _faelleFuer) void ladeFaelle();
}

/** Ein Stand aus dem Protokoll (Drahtzahlen) → Store. Ein Stand, den die Ansicht
 *  nicht lesen kann (Zustand unbekannt), ändert nichts. */
function ausProtokoll(daten: Record<string, unknown>): void {
  const s = wStandAusProtokoll(skaliere<DruckwaechterStand>("druckwaechter.stand", daten), isoAusMs);
  if (s) uebernimm(s);
}

async function refresh(): Promise<void> {
  try {
    const r = await fetch("/v2-api/druckwaechter", { credentials: "include" });
    if (!r.ok) return;
    uebernimm((await r.json()) as WStand);
  } catch {
    // Dienst startet neu -- der nächste Abgleich holt es nach.
  }
}

let _startLauf = 0;

export function startWaechter(): void {
  if (_started) return;
  _started = true;
  const lauf = ++_startLauf;
  // Darf mitten im Zeichnen gerufen werden (Einstellungen: lies()/info()
  // eines Eintrags). Der gespeicherte Stand kommt darum erst danach in den
  // Store -- sonst bricht Svelte die ganze Seite ab (state_unsafe_mutation;
  // 21.10.2026: die Einstellungen blieben leer, sobald ein Stand im Browser
  // lag).
  queueMicrotask(() => {
    if (lauf !== _startLauf) return; // inzwischen gestoppt (oder neu gestartet)
    registerRevalidator(refresh);
    const cached = loadPersisted<WStand>(STORAGE_KEY);
    if (cached?.zustand) _s.set("current", cached);
    void refresh();
    _unsubscribe = abonniere(
      "druckwaechter.stand",
      { rate: "normal" },
      { stand: (_instanz, daten) => ausProtokoll(daten), luecke: () => void refresh() },
    );
  });
}

export function stopWaechter(): void {
  _startLauf++;
  _started = false;
  _unsubscribe?.();
  _unsubscribe = undefined;
  _s.clear();
  _faelle.clear();
}

export function getWaechter(): WStand | undefined {
  return _s.get("current");
}

// ── Fälle ──────────────────────────────────────────────────────────

export async function ladeFaelle(): Promise<void> {
  _faelleFuer = _s.get("current")?.letzter_fall;
  try {
    _faelle.set("current", (await getJSON<Fall[]>("/v2-api/druckwaechter/faelle?limit=200")) ?? []);
  } catch {
    if (!_faelle.has("current")) _faelle.set("current", []);
  }
}

export function getFaelle(): Fall[] | undefined {
  return _faelle.get("current");
}

/** Das zuletzt bewertete Bild; die Zeit macht die Adresse je Takt neu. */
export function sichtBild(zeit: string): string {
  return `/v2-api/druckwaechter/sicht?t=${encodeURIComponent(zeit)}`;
}

export function fallBild(id: string): string {
  return `/v2-api/druckwaechter/faelle/${encodeURIComponent(id)}/bild`;
}

/** Tims Urteil am Fall; "" nimmt es zurück. */
export async function rueckmeldung(id: string, urteil: "fehler" | "fehlalarm" | ""): Promise<void> {
  const f = await postJSON<Fall>(`/v2-api/druckwaechter/faelle/${encodeURIComponent(id)}/rueckmeldung`, { urteil });
  const liste = _faelle.get("current");
  if (f && liste) _faelle.set("current", liste.map((x) => (x.id === f.id ? f : x)));
}

export async function stumm(an: boolean): Promise<void> {
  const s = await postJSON<WStand>("/v2-api/druckwaechter/stumm", { an });
  if (s) uebernimm(s);
}

/** Ein Wert der Einstellung; der Rest bleibt, wie er ist. */
export async function setzeEinstellung<K extends keyof Einstellung>(feld: K, wert: Einstellung[K]): Promise<void> {
  const e = _s.get("current")?.einstellung;
  if (!e) throw new Error("Druckerwächter noch nicht geladen");
  const s = await putJSON<WStand>("/v2-api/druckwaechter/einstellung", { ...e, [feld]: wert });
  if (s) uebernimm(s);
}
